<template>
  <transition :name="animation">
    <div class="loading-overlay is-active" :class="{ 'is-full-page': isFullPage }" v-if="isActive">
      <div class="loading-background" @click="cancel"/>
      <div class="loading-icon"/>
    </div>
  </transition>
</template>

<script>
import { removeElement } from "../utils/helpers";
import { HTMLElement } from "../utils/ssr";

export default {
  name: "BLoading",
  props: {
    active: Boolean,
    programmatic: Boolean,
    container: [Object, Function, HTMLElement],
    isFullPage: {
      type: Boolean,
      default: true
    },
    animation: {
      type: String,
      default: "fade"
    },
    canCancel: {
      type: Boolean,
      default: false
    },
    onCancel: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      isActive: this.active || false
    };
  },
  watch: {
    active(value) {
      this.isActive = value;
    }
  },
  methods: {
    /**
     * Close the Modal if canCancel.
     */
    cancel() {
      if (!this.canCancel || !this.isActive) return;
      this.close();
    },
    /**
     * Emit events, and destroy modal if it's programmatic.
     */
    close() {
      this.onCancel.apply(null, arguments);
      this.$emit("close");
      this.$emit("update:active", false);
      // Timeout for the animation complete before destroying
      if (this.programmatic) {
        this.isActive = false;
        setTimeout(() => {
          this.$destroy();
          removeElement(this.$el);
        }, 150);
      }
    },
    /**
     * Keypress event that is bound to the document.
     */
    keyPress(event) {
      // Esc key
      if (event.keyCode === 27) this.cancel();
    }
  },
  created() {
    if (typeof window !== "undefined") {
      document.addEventListener("keyup", this.keyPress);
    }
  },
  beforeMount() {
    // Insert the Loading component in body tag
    // only if it's programmatic
    if (this.programmatic) {
      if (!this.container) {
        document.body.appendChild(this.$el);
      } else {
        this.isFullPage = false;
        this.container.appendChild(this.$el);
      }
    }
  },
  mounted() {
    if (this.programmatic) this.isActive = true;
  },
  beforeDestroy() {
    if (typeof window !== "undefined") {
      document.removeEventListener("keyup", this.keyPress);
    }
  }
};
</script>

<style lang="scss" scoped>
@keyframes spinAround {
  from {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  to {
    -webkit-transform: rotate(359deg);
    transform: rotate(359deg);
  }
}

$grey-lighter: #cccaca;
$radius-rounded: 290486px !default;

@mixin overlay($offset: 0) {
  bottom: $offset;
  left: $offset;
  position: absolute;
  right: $offset;
  top: $offset;
  z-index: 10;
}

@mixin loader() {
  animation: spinAround 500ms infinite linear;
  border: 2px solid $grey-lighter;
  border-radius: $radius-rounded;
  border-right-color: transparent;
  border-top-color: transparent;
  content: "";
  display: block;
  height: 1em;
  position: relative;
  width: 1em;
}

.loading-overlay {
  @include overlay;
  align-items: center;
  display: none;
  justify-content: center;
  overflow: hidden;
  &.is-active {
    display: flex;
  }
  &.is-full-page {
    z-index: 999;
    position: fixed;
    .loading-icon {
      &:after {
        top: calc(50% - 2.5em);
        left: calc(50% - 2.5em);
        width: 5em;
        height: 5em;
      }
    }
  }
  .loading-background {
    @include overlay;
    background: rgba(0, 0, 0, 0.1);
  }
  .loading-icon {
    position: relative;
    &:after {
      @include loader;
      position: absolute;
      top: calc(50% - 1.5em);
      left: calc(50% - 1.5em);
      width: 3em;
      height: 3em;
      border-width: 0.25em;
    }
  }
}
</style>
